import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Search, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { groupedArticles, searchArticles } from "./docsContent";

/**
 * Docs navigation: a search box over all articles plus an audience-grouped
 * list. Mirrors the AdminSidebar layout — horizontal scroll on mobile,
 * vertical rail on desktop — so it reads as native app chrome.
 */
export function DocsSidebar({ activeSlug }: { activeSlug?: string }) {
  const [query, setQuery] = useState("");
  const groups = useMemo(() => groupedArticles(), []);
  const results = useMemo(() => (query.trim().length >= 2 ? searchArticles(query) : null), [query]);

  return (
    <aside
      data-testid="docs-sidebar"
      className="w-full shrink-0 border-b border-rule bg-surface px-3 py-3 shadow-sm md:w-64 md:border-b-0 md:border-r md:py-4 md:shadow-none"
    >
      <div className="relative mb-3">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-ink-muted" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search help…"
          aria-label="Search help articles"
          data-testid="docs-search-input"
          className="w-full rounded border border-rule bg-field py-1.5 pl-8 pr-8 text-sm text-ink placeholder:text-ink-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-ink-muted hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="size-4" />
          </button>
        )}
      </div>

      {results ? (
        <SearchResults query={query} hits={results} />
      ) : (
        <nav
          aria-label="Help topics"
          data-testid="docs-nav"
          className="flex snap-x gap-4 overflow-x-auto scroll-px-2 pb-1 md:flex-col md:gap-4 md:overflow-visible md:pb-0"
        >
          {groups.map(({ audience, articles }) => (
            <div key={audience.id} className="min-w-max md:min-w-0" data-docs-group={audience.id}>
              <h3 className="mb-1 px-2 font-display text-[0.6rem] uppercase tracking-wider text-ink-muted">
                {audience.label}
              </h3>
              <ul className="flex gap-1 md:flex-col md:gap-0.5">
                {articles.map((a) => (
                  <li key={a.slug}>
                    <Link
                      to="/docs/$slug"
                      params={{ slug: a.slug }}
                      data-testid={`docs-nav-${a.slug}`}
                      data-active={a.slug === activeSlug ? "true" : undefined}
                      className="block min-w-[10rem] snap-start rounded px-2 py-1.5 text-sm text-ink-muted transition-colors hover:bg-field hover:text-ink data-[active=true]:bg-accent data-[active=true]:text-accent-ink md:min-w-0"
                    >
                      {a.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      )}
    </aside>
  );
}

function SearchResults({
  query,
  hits,
}: {
  query: string;
  hits: ReturnType<typeof searchArticles>;
}) {
  if (hits.length === 0) {
    return (
      <p className="px-2 py-4 text-sm text-ink-muted" data-testid="docs-search-empty">
        No results for “{query}”.
      </p>
    );
  }
  return (
    <ul className="space-y-1" data-testid="docs-search-results">
      {hits.map(({ article, snippet }) => (
        <li key={article.slug}>
          <Link
            to="/docs/$slug"
            params={{ slug: article.slug }}
            className={cn(
              "block rounded px-2 py-1.5 transition-colors hover:bg-field",
            )}
          >
            <span className="block text-sm font-medium text-ink">{article.title}</span>
            <span className="mt-0.5 block text-xs text-ink-muted">{snippet}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
}
